import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
} from '@mui/material';
import { colors } from '../theme';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import HomeIcon from '@mui/icons-material/Home';
import EvStationIcon from '@mui/icons-material/EvStation';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import HubIcon from '@mui/icons-material/Hub';

const dataBlue = '#2196F3';
const inkDark = '#1a1a1a';

// Bumpy brain-shaped outline for the main hub, in unit coordinates
// (centred on 0,0 — scaled/translated where used)
const BRAIN_PATH =
  'M 0 -1 ' +
  'C 0.16 -1.18, 0.45 -1.12, 0.55 -0.95 ' +
  'C 0.80 -1.02, 1.00 -0.85, 0.95 -0.62 ' +
  'C 1.15 -0.55, 1.20 -0.28, 1.02 -0.15 ' +
  'C 1.18 0.05, 1.12 0.35, 0.92 0.42 ' +
  'C 1.00 0.65, 0.85 0.88, 0.60 0.82 ' +
  'C 0.55 1.02, 0.28 1.10, 0.12 0.95 ' +
  'C 0.05 1.05, -0.05 1.05, -0.12 0.95 ' +
  'C -0.28 1.10, -0.55 1.02, -0.60 0.82 ' +
  'C -0.85 0.88, -1.00 0.65, -0.92 0.42 ' +
  'C -1.12 0.35, -1.18 0.05, -1.02 -0.15 ' +
  'C -1.20 -0.28, -1.15 -0.55, -0.95 -0.62 ' +
  'C -1.00 -0.85, -0.80 -1.02, -0.55 -0.95 ' +
  'C -0.45 -1.12, -0.16 -1.18, 0 -1 Z';

/* ------------------------------------------------------------------ */
/* Animated system diagram (desktop / tablet)                          */
/* ------------------------------------------------------------------ */

function NodePill({ x, y, label }) {
  const w = label.length * 11 + 34;
  return (
    <g>
      <rect
        x={x - w / 2}
        y={y - 16}
        width={w}
        height={32}
        rx={16}
        fill={colors.green}
      />
      <text
        x={x}
        y={y + 6}
        textAnchor="middle"
        fill="#fff"
        fontSize="17"
        fontWeight="700"
        style={{ letterSpacing: 1 }}
      >
        {label}
      </text>
    </g>
  );
}

function Arrow({ d, color, dashed, marker }) {
  return (
    <path
      d={d}
      fill="none"
      stroke={color}
      strokeWidth={dashed ? 1.5 : 2}
      strokeLinecap="round"
      className={dashed ? 'dataLine' : undefined}
      markerEnd={`url(#${marker})`}
    />
  );
}

function SystemDiagram() {
  return (
    <Box
      sx={{
        display: { xs: 'none', sm: 'block' },
        '& .dataLine': {
          strokeDasharray: '6 6',
          animation: 'scDashFlow 1.1s linear infinite',
        },
        '@keyframes scDashFlow': {
          to: { strokeDashoffset: -12 },
        },
        '& .hubPulse': {
          animation: 'scHubPulse 3s ease-in-out infinite',
          transformOrigin: '420px 300px',
        },
        '@keyframes scHubPulse': {
          '0%, 100%': { opacity: 0.35 },
          '50%': { opacity: 0.75 },
        },
      }}
    >
      <svg viewBox="0 0 840 640" width="100%" role="img" aria-label="SolaCharge main hub balancing power between grid, solar, battery, home and EV charger">
        <defs>
          <marker id="mPower" markerWidth="6" markerHeight="6" refX="4.5" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 Z" fill={inkDark} />
          </marker>
          <marker id="mSolar" markerWidth="6" markerHeight="6" refX="4.5" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 Z" fill={colors.yellow} />
          </marker>
          <marker id="mData" markerWidth="7" markerHeight="7" refX="4.5" refY="3.5" orient="auto">
            <path d="M0,0 L7,3.5 L0,7 Z" fill={dataBlue} />
          </marker>
        </defs>

        {/* ---------- GRID node (top) ---------- */}
        <g stroke={colors.darkGreen} strokeWidth="3" fill="none" strokeLinecap="round">
          {/* pylon */}
          <path d="M400 95 L412 35 L428 35 L440 95" />
          <path d="M404 75 L436 75 M407 55 L433 55 M404 75 L436 55 M436 75 L404 55" />
          <path d="M385 55 L455 55" strokeWidth="2.5" />
          <path d="M385 55 Q393 68 401 60 M455 55 Q447 68 439 60" strokeWidth="2" stroke={colors.green} />
        </g>
        <NodePill x={420} y={118} label="GRID" />

        {/* Grid <-> Hub arrows */}
        <Arrow d="M432 148 L432 204" color={inkDark} marker="mPower" />
        <Arrow d="M408 204 L408 148" color={colors.yellow} marker="mSolar" />

        {/* METER (CT) clamped on the grid line */}
        <ellipse cx="420" cy="178" rx="26" ry="12" fill="#fff" stroke={colors.green} strokeWidth="2" />
        <text x="380" y="183" textAnchor="end" fontSize="14" fontWeight="700" fill={inkDark} style={{ letterSpacing: 1 }}>
          METER (CT)
        </text>
        {/* Meter data line to hub */}
        <Arrow d="M448 172 Q540 152 528 232" color={dataBlue} dashed marker="mData" />

        {/* ---------- SOLAR node (top right) ---------- */}
        <g>
          <circle cx="626" cy="46" r="12" fill="none" stroke={colors.yellow} strokeWidth="3" />
          <g stroke={colors.yellow} strokeWidth="2.5" strokeLinecap="round">
            <path d="M626 26 L626 20 M626 66 L626 72 M606 46 L600 46 M646 46 L652 46 M612 32 L608 28 M640 32 L644 28 M612 60 L608 64" />
          </g>
          {/* panel */}
          <g stroke={colors.darkGreen} strokeWidth="2.5" fill="none">
            <path d="M652 30 L730 30 L742 66 L664 66 Z" />
            <path d="M678 30 L690 66 M704 30 L716 66 M658 42 L734 42 M662 54 L738 54" strokeWidth="1.8" />
            <path d="M703 66 L703 80 M690 80 L716 80" />
          </g>
        </g>
        <text x="695" y="102" textAnchor="middle" fontSize="15" fontWeight="700" fill={inkDark} style={{ letterSpacing: 1.5 }}>
          SOLAR
        </text>

        {/* Solar -> Battery arrows */}
        <Arrow d="M682 112 L682 168" color={colors.yellow} marker="mSolar" />
        <Arrow d="M710 112 L710 168" color={colors.yellow} marker="mSolar" />

        {/* ---------- BATTERY node (right) ---------- */}
        <g>
          <rect x="655" y="190" width="34" height="78" rx="4" fill="none" stroke={colors.darkGreen} strokeWidth="3" />
          <rect x="663" y="206" width="18" height="8" rx="3" fill="none" stroke={colors.green} strokeWidth="2" />
          <rect x="697" y="182" width="46" height="86" rx="4" fill="none" stroke={colors.darkGreen} strokeWidth="3" />
          <rect x="706" y="194" width="28" height="10" rx="2" fill="none" stroke={colors.green} strokeWidth="2" />
          <circle cx="720" cy="236" r="12" fill={colors.yellow} opacity="0.9" />
        </g>
        <NodePill x={700} y={300} label="BATTERY" />

        {/* Battery <-> Hub arrows */}
        <Arrow d="M640 282 L530 282" color={colors.yellow} marker="mSolar" />
        <Arrow d="M530 312 L640 312" color={inkDark} marker="mPower" />
        {/* Battery data line */}
        <Arrow d="M700 330 Q700 380 560 366" color={dataBlue} dashed marker="mData" />

        {/* ---------- HOME node (left) ---------- */}
        <g stroke={colors.darkGreen} strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round">
          <path d="M60 235 L125 185 L190 235" />
          <path d="M72 228 L72 292 L178 292 L178 228" />
          {/* appliances */}
          <rect x="88" y="240" width="30" height="18" rx="4" strokeWidth="2.2" />
          <path d="M92 262 Q103 270 114 262" strokeWidth="2" stroke={colors.green} />
          <circle cx="152" cy="252" r="11" strokeWidth="2.2" />
          <path d="M148 270 L156 270" strokeWidth="2" />
        </g>
        <NodePill x={125} y={322} label="HOME" />

        {/* Hub -> Home arrows */}
        <Arrow d="M330 282 L212 282" color={colors.yellow} marker="mSolar" />
        <Arrow d="M330 312 L212 312" color={inkDark} marker="mPower" />

        {/* ---------- EV CHARGER node (bottom) ---------- */}
        <g stroke={colors.darkGreen} strokeWidth="3" fill="none">
          <rect x="390" y="448" width="60" height="104" rx="28" />
          <rect x="406" y="466" width="28" height="12" rx="5" strokeWidth="2.2" />
          <circle cx="420" cy="520" r="15" strokeWidth="2.5" />
          <circle cx="420" cy="492" r="6" fill={colors.yellow} stroke="none" />
        </g>
        {/* car */}
        <g stroke={colors.darkGreen} strokeWidth="3" fill="none" strokeLinecap="round">
          <path d="M250 530 Q252 512 270 508 L282 492 Q292 480 310 482 L330 486 Q344 490 348 504 L352 512 Q362 516 360 530 Z" />
          <circle cx="278" cy="532" r="10" />
          <circle cx="336" cy="532" r="10" />
        </g>
        <path d="M312 488 L304 502 L312 502 L302 518 L318 500 L310 500 L318 488 Z" fill={colors.yellow} />
        {/* charger -> car */}
        <Arrow d="M382 505 L370 505" color={inkDark} marker="mPower" />
        <NodePill x={420} y={588} label="EV CHARGER" />

        {/* Hub -> EVC arrows */}
        <Arrow d="M408 392 L408 435" color={colors.yellow} marker="mSolar" />
        <Arrow d="M432 392 L432 435" color={inkDark} marker="mPower" />
        {/* EVC data line */}
        <Arrow d="M508 358 Q560 430 462 470" color={dataBlue} dashed marker="mData" />

        {/* ---------- MAIN HUB (center, brain outline) ---------- */}
        <g transform="translate(420 300) scale(88 72)">
          <path
            d={BRAIN_PATH}
            fill="#fff"
            stroke={colors.darkGreen}
            strokeWidth="3.5"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
          {/* hemisphere grooves */}
          <path
            d="M 0 -1 C 0.06 -0.82, -0.06 -0.66, 0.02 -0.5 M 0 0.98 C -0.05 0.82, 0.05 0.68, 0 0.54"
            fill="none"
            stroke={colors.darkGreen}
            strokeWidth="1.8"
            strokeLinecap="round"
            opacity="0.5"
            vectorEffect="non-scaling-stroke"
          />
        </g>
        <g transform="translate(420 300) scale(95 78)" className="hubPulse">
          <path
            d={BRAIN_PATH}
            fill="none"
            stroke={colors.green}
            strokeWidth="2"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </g>
        {/* circuit dots */}
        <g fill={colors.darkGreen}>
          <circle cx="352" cy="258" r="4" />
          <circle cx="488" cy="258" r="4" />
          <circle cx="344" cy="330" r="4" />
          <circle cx="496" cy="330" r="4" />
          <circle cx="420" cy="234" r="4" />
          <circle cx="420" cy="366" r="4" />
        </g>
        <g stroke={colors.darkGreen} strokeWidth="1.6" opacity="0.65">
          <path d="M352 258 L378 272 M488 258 L462 272 M344 330 L374 322 M496 330 L466 322 M420 234 L420 250 M420 366 L420 350" />
        </g>
        {/* sun logo */}
        <path d="M382 292 A38 38 0 0 1 458 292 Z" fill={colors.yellow} />
        <text x="420" y="316" textAnchor="middle" fontSize="19" fontWeight="800" style={{ letterSpacing: 0.5 }}>
          <tspan fill={inkDark}>SOLA</tspan>
          <tspan fill={colors.green}>CHARGE</tspan>
        </text>
        <text x="420" y="340" textAnchor="middle" fontSize="14" fontWeight="700" fill={inkDark} style={{ letterSpacing: 2 }}>
          MAIN HUB
        </text>

        {/* ---------- Legend ---------- */}
        <g transform="translate(30, 628)">
          <path d="M0 0 L38 0" stroke={dataBlue} strokeWidth="1.5" className="dataLine" markerEnd="url(#mData)" />
          <text x="48" y="5" fontSize="14" fontWeight="600" fill={inkDark}>DATA</text>
          <path d="M110 0 L148 0" stroke={inkDark} strokeWidth="2" markerEnd="url(#mPower)" />
          <text x="158" y="5" fontSize="14" fontWeight="600" fill={inkDark}>POWER</text>
          <path d="M234 0 L272 0" stroke={colors.yellow} strokeWidth="2" markerEnd="url(#mSolar)" />
          <text x="282" y="5" fontSize="14" fontWeight="600" fill={inkDark}>SOLAR</text>
        </g>
      </svg>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* Compact stacked flow (mobile)                                       */
/* ------------------------------------------------------------------ */

function MobileFlow() {
  const nodes = [
    {
      icon: <WbSunnyIcon />,
      title: 'Solar',
      text: 'Panels generate clean power, stored in the battery',
      color: colors.yellow,
    },
    {
      icon: <BatteryChargingFullIcon />,
      title: 'Battery (ESS)',
      text: 'Stores solar, discharges when needed or tops up from grid',
      color: colors.green,
    },
    {
      icon: <ElectricalServicesIcon />,
      title: 'Grid + Meter',
      text: 'CT sensor monitors import/export and spare capacity',
      color: dataBlue,
    },
    {
      icon: <HubIcon />,
      title: 'SolaCharge Main Hub',
      text: 'Balances every source and load in real time',
      color: colors.green,
      highlight: true,
    },
    {
      icon: <HomeIcon />,
      title: 'Home Loads',
      text: 'A/C, lighting, appliances always take priority',
      color: colors.yellow,
    },
    {
      icon: <EvStationIcon />,
      title: 'EV Charger',
      text: 'Charges from solar or grid, balanced to available capacity',
      color: colors.green,
    },
  ];

  return (
    <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 1.5 }}>
      {nodes.map((node, i) => (
        <React.Fragment key={node.title}>
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              borderRadius: 3,
              border: node.highlight
                ? `2px solid ${colors.green}`
                : '1px solid #e0e0e0',
              bgcolor: node.highlight ? `${colors.green}0D` : '#fff',
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                p: 1.2,
                borderRadius: 2,
                bgcolor: `${node.color}20`,
                color: node.color === colors.yellow ? '#b98f00' : node.color,
                flexShrink: 0,
              }}
            >
              {node.icon}
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: colors.black }}>
                {node.title}
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', color: '#666', lineHeight: 1.4 }}>
                {node.text}
              </Typography>
            </Box>
          </Paper>
          {i < nodes.length - 1 && (
            <Typography sx={{ textAlign: 'center', color: colors.green, lineHeight: 0.6, fontSize: '1.1rem' }}>
              ↓
            </Typography>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* Section — "The Brain of Your Power System"                          */
/* ------------------------------------------------------------------ */

function TechnologySection() {
  return (
    <Box
      id="technology"
      sx={{
        py: { xs: 6, md: 10 },
        bgcolor: '#f8f8f8',
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Chip
            label="DYNAMIC LOAD BALANCING & POWER MANAGEMENT"
            sx={{
              bgcolor: `${colors.green}15`,
              color: colors.darkGreen,
              fontWeight: 700,
              letterSpacing: 1,
              fontSize: '0.75rem',
              mb: 2,
            }}
          />
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.75rem' },
              fontWeight: 800,
              mb: 2,
              color: colors.black,
            }}
          >
            The{' '}
            <Box component="span" sx={{ color: colors.green }}>
              Brain
            </Box>{' '}
            of Your Power System
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#666',
              maxWidth: '760px',
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.2rem' },
            }}
          >
            One intelligent hub connects your grid, solar, battery, home and EV
            charger — every watt goes where it matters most, in real time
          </Typography>
        </Box>

        {/* System diagram */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 4,
            border: '1px solid #e6e6e6',
            bgcolor: '#fff',
          }}
        >
          <SystemDiagram />
          <MobileFlow />
        </Paper>
      </Container>
    </Box>
  );
}

export default TechnologySection;
